/* eslint-disable */
// import React, { useEffect } from "react";
import { BehaviorSubject, Observable, combineLatest } from "rxjs";
import {
  IComponentContext,
  UpdateCommand,
  ComponentContextReadyState,
  DataflowIntegration,
  ComponentContextCreateOptions,
  SingleValueParameter,
  ReportDefinition,
  MultiValueParameter,
  ParameterBase,
  MultiplicityType,
} from "@sa/rep-core";
import { dataService } from "../../dataFlow";
import { Cube, CubeDefinition, OLAPResult } from "@sa/rep-olap";
import { MDDEResult, SortingOptions } from "@sa/rep-mdde";
import { SelectionMode, AxisPosition, AggregationType, SortingDirection, DataType } from "@sa/rep-common";
import { ComponentContextCreateOptionsBasf, MDDEQuery, OLAPTransformer, ServicesRegister } from "../../shared";
import { debounceTime } from "rxjs/operators";
import { CUBE_ID } from "./constants";
import { clone, uniq } from "../../../../shared";
import { last } from "lodash";
import { DictionariesService } from "@sa/rep-mdm";

export class ContextReport1 implements IComponentContext {
  public id: string;
  public readonly options: ComponentContextCreateOptionsBasf;
  public readonly dataflowIntegration: DataflowIntegration;
  public readyState: ComponentContextReadyState;
  protected _exportParameters = new Map<string, ParameterBase>();

  protected cube = new BehaviorSubject<CubeDefinition | null>(null);
  public olapCountriesPopulation$ = new BehaviorSubject<OLAPResult | null>(null);
  public olapCountriesSelfSufficiency$ = new BehaviorSubject<OLAPResult | null>(null);
  public olapCountriesConsumtion$ = new BehaviorSubject<OLAPResult | null>(null);

  public olapWorldPopulation$ = new BehaviorSubject<OLAPResult | null>(null);

  public sources$ = new BehaviorSubject<string | null>(null);
  public orderedCountriesIds$ = new BehaviorSubject<number[]>([]);

  public highlightData = new BehaviorSubject<Record<string, string>>({});

  protected _calendar: BehaviorSubject<string[]>;
  public get calendar(): Observable<string[]> {
    return this._calendar.asObservable();
  }

  protected _commodities: BehaviorSubject<string[]>;
  public get commodities(): Observable<string[]> {
    return this._commodities.asObservable();
  }

  constructor(id: string, createOptions: ComponentContextCreateOptionsBasf) {
    this.id = id;
    this.options = createOptions;

    this.dataflowIntegration = {
      canIntegrate: true, // Указываем, что компонент интегрируется к Dataflow сервису (и может управляться с помощью параметров отчёта)
      integrated: false,
    };

    this.readyState = ComponentContextReadyState.Loading;

    // const calendarParameter = createOptions.dataflowService.getParameter("@calendar") as MultiValueParameter;
    this._calendar = new BehaviorSubject([] as string[]);
    const commoditiesParameter = createOptions.dataflowService.getParameter("@commodities") as MultiValueParameter;
    this._commodities = new BehaviorSubject(commoditiesParameter.value as string[]);

    combineLatest([this._calendar, this._commodities])
      .pipe(debounceTime(500))
      .subscribe(([calendar, commodities]) => {
        if (calendar?.length) {
          this.query({ calendar, commodities });
        }
      });

    const exportParameters = [
      "@e_calendar", "@e_countries"
    ];
    for (const eParamName of exportParameters) {
      let eParameter = this.options.dataflowService.getParameter(eParamName);
      if (eParameter === undefined) {
        this.options.dataflowService.parameters.add({
          id: eParamName,
          multiplicityType: MultiplicityType.Multiple,
          type: DataType.Number,
          allowNullable: false,
          value: []
        });
        eParameter = this.options.dataflowService.getParameter(eParamName);
      }
      this._exportParameters.set(eParamName, eParameter!);
    }
  }

  async tryIntegrate() {
    if (this.dataflowIntegration.canIntegrate && !this.dataflowIntegration.integrated) {

      this.dataflowIntegration.integrated = true;
      const cube = await dataService.getCubeDefinition(CUBE_ID);
      this.cube.next(cube);

    }
  }

  async update(command: UpdateCommand): Promise<void> {
    const calendar = command.values.get("@calendar");
    if (calendar !== undefined) {
      this._calendar.next(calendar as string[]);
    }
    const commodities = command.values.get("@commodities");
    if (commodities !== undefined) {
      this._commodities.next(commodities as string[]);
    }
  }

  async query(parameters: { calendar: string[]; commodities: string[]; }) {
    const { calendar, commodities } = parameters;
    const calendarId = Number(calendar[0]);
    this.readyState = ComponentContextReadyState.Loading;
    this.olapCountriesPopulation$.next(null);
    this.olapCountriesSelfSufficiency$.next(null);

    const { dataSourcesService } = this.options;
    const commodityId = Number(commodities[0]);
    const sources = await dataSourcesService.getDataSources(26, 1, commodityId);
    this.sources$.next(sources);

    const defPoputation = await dataService.getCubeDefinition("population");
    const cubePopulation = await dataService.getCube({
      id: "population",
      parameters: defPoputation.parameters,
    });

    const dictionaryService = this.options.serviceProvider.resolve(ServicesRegister.Dictionary) as DictionariesService;
    const dictSlideCountries = await dictionaryService.getDictionary({
      id: 'slides-countries-display',
      parameters: {
        'slide': 26,
        'olap_commodity': Number(commodities[0]),
        'business_unit': 1
      }
    });
    const itemsCountriesRelationship = new Map((await dictSlideCountries.getAll()).map(x => [Number(x.getValue("olap_country")), Number(x.id)]));
    const itemsSlideCountries = Array.from(itemsCountriesRelationship.keys());
    const calendarAttempts = [Number(last(calendar)) + 100000, Number(last(calendar))]; // Прогнозируемый, если нет, то текущий
    let itemsTopCountries: number[] = [];
    for (const calendarAttempt of calendarAttempts) {
      const dictTopCountries = await dictionaryService.getDictionary({
        id: 'dict_f_lead_population_countries',
        parameters: {
          'p_calendar': calendarAttempt,
          'p_global_unit': 1
        }
      });
      itemsTopCountries = (await dictTopCountries.getAll()).map(x => Number(x.id));
      if (itemsTopCountries.length) {
        break;
      }
    }
    const itemsCountries = itemsTopCountries.filter(id => itemsSlideCountries.includes(id));
    let countries = itemsCountries.slice(0, 14);
    if (countries.length === 0) {
      countries = itemsTopCountries.slice(0, 10);
    }
    this.orderedCountriesIds$.next(countries);

    const worldCountry = 100231;
    const cubeSlideDef = await dataService.getCubeDefinition("slide_25");
    const cubeParametersSelfSufficiency = clone(cubeSlideDef.parameters);
    cubeParametersSelfSufficiency["p_commodities"]!["defaultValue"] = commodities.join(',');
    cubeParametersSelfSufficiency["p_countries"]!["defaultValue"] = countries.join(',');
    cubeParametersSelfSufficiency["p_attributes"]!["defaultValue"] = [100].join(',');
    cubeParametersSelfSufficiency["p_calendar"]!["defaultValue"] = [-6, -4, -2, 0, +5, +10].map(x => calendarId + (x * 10000)).join(',');
    const cubeSlideSelfSufficiency = await dataService.getCube({
      id: "slide_25",
      parameters: cubeParametersSelfSufficiency,
    });
    const cubeParametersConsumtion = clone(cubeSlideDef.parameters);
    cubeParametersConsumtion["p_commodities"]!["defaultValue"] = commodities.join(',');
    cubeParametersConsumtion["p_countries"]!["defaultValue"] = countries.join(',');
    cubeParametersConsumtion["p_attributes"]!["defaultValue"] = [30].join(',');
    cubeParametersConsumtion["p_calendar"]!["defaultValue"] = [-6, -4, -2, 0, +5, +10].map(x => calendarId + (x * 10000)).join(',');
    const cubeSlideConsumtion = await dataService.getCube({
      id: "slide_25",
      parameters: cubeParametersConsumtion,
    }); 
    const eCountriesParameter = this._exportParameters.get("@e_countries")! as MultiValueParameter;
    eCountriesParameter.value = countries.map(countryId => itemsCountriesRelationship.get(countryId));
    const eCalendarParameter = this._exportParameters.get("@e_calendar")! as MultiValueParameter;
    eCalendarParameter.value = [-6, -4, -2, 0, +5, +10].map(x => calendarId + (x * 10000));


    const olapResultPopulation$ = cubePopulation.query({
      columns: {
        showEmpty: false,
        parts: [
          {
            path: "countries.default",
            selection: countries.length ? countries.concat(worldCountry) : null,
            selectionMode: SelectionMode.Include,
          },
        ],
      },
      filters: {
        showEmpty: false,
        parts: [
          { path: "units.default", selection: [4], selectionMode: SelectionMode.Include },
        ],
      },
      rows: {
        showEmpty: false,
        parts: [
          { path: "calendar.default", selection: [-6, -4, -2, 0, +5, +10].map(x => calendarId + (x * 10000)), selectionMode: SelectionMode.Include },
          { path: "Measures", measures: [{ id: "value", aggregationType: AggregationType.Sum }] },
        ],
      },
      sourceResolveOptions: {
        sourceId: cubePopulation.id,
      }
    } as any);

    const [olapResultSlideSelfSufficiency$, olapResultSlideConsumtion$] = [
      [100, cubeSlideSelfSufficiency], [30, cubeSlideConsumtion]
    ].map(([attributeId, cube]) => (cube as Cube).query({
      columns: {
        showEmpty: false,
        parts: [
          {
            path: "countries.default",
            selection: countries,
            selectionMode: SelectionMode.Include,
          },
        ],
      },
      filters: {
        showEmpty: false,
        parts: [
          { path: "commodities.default", selection: commodities, selectionMode: SelectionMode.Include },
        ],
      },
      rows: {
        showEmpty: false,
        parts: [
          { path: "attributes.default", selection: [attributeId], selectionMode: SelectionMode.Include },
          {
            path: "calendar.default",
            selection: [-6, -4, -2, 0, +5, +10].map(x => calendarId + (x * 10000)),
            selectionMode: SelectionMode.Include,
          },
          { path: "Measures", measures: [{ id: "value", aggregationType: AggregationType.Sum }] },
        ],
      },
      sourceResolveOptions: {
        sourceId: (cube as Cube).id,
      },
    } as any));

    let [olapResultPopulation, olapResultSlideSelfSufficiency, olapResultSlideConsumtion] = await Promise.all([olapResultPopulation$, olapResultSlideSelfSufficiency$, olapResultSlideConsumtion$]);

    const worldPopulationResult = clone(olapResultPopulation);
    worldPopulationResult.data.aggregatedSelection['countries.default'] = worldPopulationResult.data.aggregatedSelection['countries.default']?.filter(
      selection => Number(selection) === worldCountry
    );
    worldPopulationResult.data.rows.forEach(row => {
      row.cells = row.cells.filter(cell => Number(cell.selection['countries.default']) === worldCountry);
    });
    const countriesPopulationResult = clone(olapResultPopulation);
    countriesPopulationResult.data.aggregatedSelection['countries.default'] = countriesPopulationResult.data.aggregatedSelection['countries.default']?.filter(
      selection => Number(selection) !== worldCountry
    );
    countriesPopulationResult.data.rows.forEach(row => {
      row.cells = row.cells.filter(cell => Number(cell.selection['countries.default']) !== worldCountry);
    });

    const countriesSelfSufficiency = clone(olapResultSlideSelfSufficiency);
    const countriesConsumtion = clone(olapResultSlideConsumtion);

    [
      [30, countriesConsumtion],
      [100, countriesSelfSufficiency],
    ].forEach(args => {
      const [attributeId, ...olapAttrsData] = args;
      for (const olapAttrData of (olapAttrsData as OLAPResult[])) {
        delete olapAttrData.data.aggregatedSelection['attributes.default'];
        olapAttrData.data.rows = olapAttrData.data.rows.filter(
          row => {
            const isAttr = Number(row.selection['attributes.default']) === (attributeId as number);
            return isAttr;
          }
        );
      }
    });

    this.olapCountriesPopulation$.next(countriesPopulationResult);
    this.olapCountriesSelfSufficiency$.next(countriesSelfSufficiency);
    this.olapCountriesConsumtion$.next(countriesConsumtion);
    this.readyState = ComponentContextReadyState.Loaded;
    this.olapWorldPopulation$.next(worldPopulationResult);
  }
}
